import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Tooltip,
  Popconfirm,
  Modal,
  Input,
  Select,
  Typography,
  Tag,
  Row,
  Col,
} from 'antd';
import {
  StarOutlined,
  StarFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  EyeOutlined,
  CheckCircleFilled,
  LockOutlined,
} from '@ant-design/icons';
import { Layout, PanelBottom, Save, FileText, Type } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import TableContainer from '@/components/TableContainer';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useTranslation } from 'react-i18next';
import { rt_list, rt_create, rt_update, rt_delete, rt_set_default, upload_file, rt_upload_logo } from './reportTemplateApi';

const resolveMediaUrl = (url) => url || '';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24];

const freshForm = () => ({
  name: '',
  fontSize: 12,
  header: { title: '', bgColor: '#204448', textColor: '#ffffff', fontSize: 14, logo: '' },
  footer: { text: '', bgColor: '#f3f4f6', textColor: '#374151', fontSize: 10 },
});

// ── System template — always present, cannot be edited or deleted ──
// Acts as default when backend returns 0 templates.
// Remove DUMMY_TEMPLATES once real API is wired up.
const SYSTEM_TEMPLATE = {
  id: '__system__',
  name: 'Nexgensis Default',
  isDefault: false,
  isSystem: true,
  fontSize: 12,
  header: { title: 'BATCH MANUFACTURING RECORD', bgColor: '#204448', textColor: '#ffffff', fontSize: 14, logo: '' },
  footer: { text: 'Confidential — For Internal Use Only  |  Page {page} of {total}', bgColor: '#f3f4f6', textColor: '#374151', fontSize: 10 },
};

const DUMMY_TEMPLATES = [
  {
    id: 2, name: 'MBPR Template', isDefault: false, fontSize: 12,
    header: { title: 'MASTER BATCH PREPARATION RECORD', bgColor: '#1d4ed8', textColor: '#ffffff', fontSize: 14, logo: '' },
    footer: { text: 'Page {page} of {total}  |  Nexgensis', bgColor: '#eff6ff', textColor: '#1d4ed8', fontSize: 10 },
  },
  {
    id: 3, name: 'Audit Report Template', isDefault: false, fontSize: 12,
    header: { title: 'AUDIT REPORT', bgColor: '#7c3aed', textColor: '#ffffff', fontSize: 14, logo: '' },
    footer: { text: '© Nexgensis — Confidential Document', bgColor: '#f5f3ff', textColor: '#5b21b6', fontSize: 10 },
  },
];

// ── All helper components defined OUTSIDE main component to prevent remount on rerender ──

const FieldLabel = ({ children, required, color }) => (
  <div style={{ fontSize: 12, color: color || '#374151', fontWeight: 600, marginBottom: 5 }}>
    {children}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
  </div>
);

const Section = ({ accentColor, icon: Icon, title, children, cardBg, borderColor, textColor }) => (
  <div style={{
    background: cardBg,
    borderRadius: 10,
    border: `1px solid ${borderColor}`,
    borderLeft: `4px solid ${accentColor}`,
    padding: '10px 14px',
    marginBottom: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
      <Icon size={15} style={{ color: accentColor }} />
      <span style={{ fontWeight: 700, fontSize: 13, color: textColor }}>{title}</span>
    </div>
    {children}
  </div>
);

const ColorPicker = ({ label, value, onChange, borderColor, inputLabelColor }) => (
  <div>
    <div style={{ fontSize: 11, color: inputLabelColor, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', width: 40, height: 36, flexShrink: 0 }}>
        <div style={{
          width: 40, height: 36, borderRadius: 8, background: value,
          border: `2px solid ${borderColor}`, cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }} />
        <input
          type="color" value={value}
          onChange={e => onChange(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
        />
      </div>
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        size="small"
        style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, maxWidth: 110 }}
        maxLength={7}
      />
    </div>
  </div>
);

const SectionPill = ({ id, icon: Icon, label, activeSection, onSelect, isDark, subText }) => {
  const active = activeSection === id;
  return (
    <button
      onClick={() => onSelect(id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
        fontWeight: 600, fontSize: 13,
        background: active ? '#204448' : (isDark ? defaultColors.palette.gray[700] : '#f1f5f9'),
        color: active ? '#ffffff' : subText,
        transition: 'all 0.18s',
        boxShadow: active ? '0 2px 8px rgba(32,68,72,0.25)' : 'none',
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
};

const DocPreview = ({ form, logoPreviewUrl, sectionBg, borderColor, subText, textColor }) => (
  <div>
    <div style={{ fontSize: 11, color: subText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
      Live Preview
    </div>
    <div style={{
      background: '#ffffff',
      borderRadius: 10,
      border: '1px solid #d1d5db',
      overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    }}>
      {/* Header band */}
      <div style={{
        background: form.header.bgColor,
        color: form.header.textColor,
        padding: '12px 18px',
        display: 'flex', alignItems: 'center', gap: 10,
        minHeight: 52,
      }}>
        {logoPreviewUrl && (
          <img
            src={logoPreviewUrl}
            alt="logo"
            style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'contain', flexShrink: 0, background: 'rgba(255,255,255,0.15)', padding: 2 }}
          />
        )}
        <span style={{ fontWeight: 800, fontSize: form.header.fontSize, lineHeight: 1.2 }}>
          {form.header.title || 'Your Header Title'}
        </span>
      </div>

      {/* Body skeleton */}
      <div style={{ padding: '16px 18px', background: '#fafafa' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          {[38, 42, 35].map((w, i) => (
            <div key={i} style={{ height: 7, borderRadius: 4, background: '#e2e8f0', width: `${w}%` }} />
          ))}
        </div>
        <div style={{ height: 1, background: '#e5e7eb', marginBottom: 12 }} />
        {[90, 75, 82, 60, 70].map((w, i) => (
          <div key={i} style={{ height: 7, borderRadius: 4, background: '#e2e8f0', width: `${w}%`, marginBottom: i < 4 ? 8 : 0 }} />
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          {[0, 1].map(i => (
            <div key={i} style={{ flex: 1, height: 24, borderRadius: 6, background: '#f1f5f9', border: '1px dashed #cbd5e1' }} />
          ))}
        </div>
      </div>

      {/* Footer band */}
      <div style={{
        background: form.footer.bgColor,
        color: form.footer.textColor,
        padding: '8px 18px',
        fontSize: form.footer.fontSize,
        borderTop: '1px solid #e5e7eb',
        minHeight: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{form.footer.text || 'Footer text will appear here'}</span>
        <span style={{ fontSize: form.footer.fontSize - 1, opacity: 0.7 }}>1 / 1</span>
      </div>
    </div>

    {/* Meta chips */}
    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
      {[
        { label: 'Base font', val: `${form.fontSize}px` },
        { label: 'Header font', val: `${form.header.fontSize}px` },
        { label: 'Footer font', val: `${form.footer.fontSize}px` },
      ].map(({ label, val }) => (
        <div key={label} style={{
          background: sectionBg, border: `1px solid ${borderColor}`,
          borderRadius: 20, padding: '2px 10px', fontSize: 11,
        }}>
          <span style={{ color: subText }}>{label}: </span>
          <span style={{ color: textColor, fontWeight: 600 }}>{val}</span>
        </div>
      ))}
    </div>
  </div>
);

// ==============================|| REPORT TEMPLATE CONFIG ||============================== //

const ReportTemplateConfig = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const accessData = accessControlUtils('Report Template Config');

  const textColor       = isDark ? '#ffffff' : '#1f2937';
  const subText         = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor     = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const cardBg          = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const sectionBg       = isDark ? defaultColors.palette.gray[750] || '#2d3748' : '#f8faff';
  const inputLabelColor = isDark ? defaultColors.palette.gray[300] : '#374151';

  const [templates, setTemplates]         = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);
  const [editingId, setEditingId]         = useState(null);
  const [form, setForm]                   = useState(freshForm());
  const [activeSection, setActiveSection] = useState('header');
  const logoInputRef                      = useRef(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');

  // System template is always first. It is default only when no user template has is_default=true.
  const tableData = useMemo(() => {
    const userHasDefault = templates.some(t => t.is_default);
    const systemRow = { ...SYSTEM_TEMPLATE, isDefault: !userHasDefault };
    return [systemRow, ...templates];
  }, [templates]);

  // ── Map backend snake_case → form camelCase ──
  const toForm = (rec) => ({
    name:     rec.name,
    fontSize: rec.base_font_size,
    header: {
      title:     rec.header?.title     || '',
      bgColor:   rec.header?.bg_color  || '#204448',
      textColor: rec.header?.text_color|| '#ffffff',
      fontSize:  rec.header?.font_size || 14,
      logo:      rec.header?.logo_url  || '',
    },
    footer: {
      text:      rec.footer?.text       || '',
      bgColor:   rec.footer?.bg_color   || '#f3f4f6',
      textColor: rec.footer?.text_color || '#374151',
      fontSize:  rec.footer?.font_size  || 10,
    },
  });

  // ── Map form camelCase → backend snake_case ──
  const toPayload = (f) => ({
    name:           f.name,
    base_font_size: f.fontSize,
    header: {
      title:      f.header.title,
      bg_color:   f.header.bgColor,
      text_color: f.header.textColor,
      font_size:  f.header.fontSize,
      logo_url:   f.header.logo || null,
    },
    footer: {
      text:       f.footer.text,
      bg_color:   f.footer.bgColor,
      text_color: f.footer.textColor,
      font_size:  f.footer.fontSize,
    },
  });

  const fetchTemplates = () => {
    setLoading(true);
    rt_list((res) => {
      setLoading(false);
      if (res?.data?.status === 'success') {
        setTemplates(res.data.data || []);
      } else {
        toast.error(res?.data?.message || 'Failed to load templates');
      }
    });
  };

  useEffect(() => { fetchTemplates(); }, []);

  const setField       = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const setHeaderField = (f, v) => setForm(p => ({ ...p, header: { ...p.header, [f]: v } }));
  const setFooterField = (f, v) => setForm(p => ({ ...p, footer: { ...p.footer, [f]: v } }));

  const openCreate = () => {
    setEditingId(null);
    setForm(freshForm());
    setLogoPreviewUrl('');
    setActiveSection('header');
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm(toForm(record));
    setLogoPreviewUrl(resolveMediaUrl(record.header?.logo_url || ''));
    setActiveSection('header');
    setModalOpen(true);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a local blob URL from the binary — use this for live preview (no server round-trip needed)
    const objectUrl = URL.createObjectURL(file);
    setLogoPreviewUrl(objectUrl);

    // If editing an existing template use the dedicated logo endpoint (preserves SVG, updates DB directly).
    // If creating a new template (no ID yet) fall back to general upload_file.
    setLogoUploading(true);

    const handleUploadResponse = (logoUrl) => {
      setLogoUploading(false);
      setHeaderField('logo', logoUrl);
      // Keep objectUrl as preview — blob URL loads from local memory, no proxy issues
      toast.success('Logo uploaded successfully');
    };
    const handleUploadError = (msg) => {
      setLogoUploading(false);
      URL.revokeObjectURL(objectUrl);
      setLogoPreviewUrl('');
      setHeaderField('logo', '');
      toast.error(msg || 'Logo upload failed');
    };

    // The system template ('__system__') has no DB row, so it can't use the
    // template-scoped upload endpoint (that route only accepts an integer id and
    // 404s otherwise). Use the generic file upload for it, like the "new template"
    // case — the returned URL is stored on the header the same way.
    if (editingId !== null && editingId !== '__system__') {
      rt_upload_logo(editingId, file, (res) => {
        if (res?.data?.status === 'success') {
          handleUploadResponse(res.data.data.header?.logo_url || '');
        } else {
          handleUploadError(res?.data?.message);
        }
      });
    } else {
      upload_file(file, (res) => {
        if (res?.data?.status === 'success') {
          handleUploadResponse(res.data.data.url || '');
        } else {
          handleUploadError(res?.data?.message);
        }
      });
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.name.trim())         { toast.error('Template name is required'); return; }
    if (!form.header.title.trim()) { toast.error('Header title is required');  return; }
    setSaving(true);
    // Strip the pending logo filename from payload — logo_url must be a real URL or null
    const payload = toPayload(form);

    if (editingId !== null) {
      rt_update(editingId, payload, (res) => {
        setSaving(false);
        if (res?.data?.status === 'success') {
          toast.success('Template updated');
          fetchTemplates();
          setModalOpen(false);
        } else {
          toast.error(res?.data?.message || 'Update failed');
        }
      });
    } else {
      rt_create(payload, (res) => {
        setSaving(false);
        if (res?.data?.status === 'success') {
          toast.success('Template created');
          fetchTemplates();
          setModalOpen(false);
        } else {
          toast.error(res?.data?.message || 'Create failed');
        }
      });
    }
  };

  const handleDelete = (id) => {
    rt_delete(id, (res) => {
      if (res?.data?.status === 'success') {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    });
  };

  const handleSetDefault = (id) => {
    if (id === '__system__') {
      // No backend call for system template — it's frontend-only
      setTemplates(p => p.map(t => ({ ...t, is_default: false })));
      toast.success('"Nexgensis Default" is now the default');
      return;
    }
    rt_set_default(id, (res) => {
      if (res?.data?.status === 'success') {
        toast.success(`"${templates.find(t => t.id === id)?.name}" is now the default`);
        fetchTemplates();
      } else {
        toast.error(res?.data?.message || 'Failed to set default');
      }
    });
  };

  const columns = [
    {
      title: '#', width: 36, align: 'center',
      render: (_, __, i) => <span style={{ color: subText, fontSize: 12 }}>{i + 1}</span>,
    },
    {
      title: 'Template Name', dataIndex: 'name', width: 300,
      render: (name, rec) => (
        <Space size={8}>
          <FileText size={15} style={{ color: rec.isSystem ? '#204448' : '#3b82f6' }} />
          <span style={{ fontWeight: 600, color: textColor }}>{name}</span>
          {rec.isSystem           && <Tag color="default" style={{ fontSize: 10, padding: '0 6px', lineHeight: '18px', borderStyle: 'dashed' }}>System</Tag>}
          {(rec.isDefault || rec.is_default) && <Tag color="success" style={{ fontSize: 10, padding: '0 6px', lineHeight: '18px' }}>Default</Tag>}
        </Space>
      ),
    },
    {
      title: 'Header Title',
      render: (_, rec) => <span style={{ fontSize: 12, color: subText }}>{rec.header?.title || '—'}</span>,
    },
    {
      title: 'Preview', width: 80, align: 'center',
      render: (_, rec) => (
        <Tooltip title="Edit & Preview">
          <Icons.Eye className="text-blue-600 w-5 h-5 cursor-pointer hover:text-blue-800" onClick={() => openEdit(rec)}/>
        </Tooltip>
      ),
    },
    {
      title: 'Action', width: 120, align: 'center', fixed: 'right',
      render: (_, rec) => {
        if (rec.isSystem) {
          return (
            <Space size="middle">
              <Tooltip title={rec.isDefault ? 'System default (active)' : 'Set as default'}>
                <span style={{ cursor: rec.isDefault ? 'default' : 'pointer' }}
                  onClick={() => { if (!rec.isDefault) handleSetDefault(rec.id); }}>
                  {rec.isDefault
                    ? <StarFilled  style={{ color: '#f59e0b', fontSize: 16 }} />
                    : <StarOutlined style={{ color: '#d1d5db', fontSize: 16 }} />}
                </span>
              </Tooltip>
              <Tooltip title="System template — cannot be edited">
                <LockOutlined style={{ color: '#d1d5db', fontSize: 15, cursor: 'not-allowed' }} />
              </Tooltip>
              <Tooltip title="System template — cannot be deleted">
                <Icons.Trash2 className="text-red-200 w-5 h-5 cursor-not-allowed" />
              </Tooltip>
            </Space>
          );
        }
        return (
          <Space size="middle">
            <Tooltip title={rec.is_default ? 'Default template' : 'Set as default'}>
              <span style={{ cursor: rec.is_default ? 'default' : 'pointer' }}
                onClick={() => { if (!rec.is_default) handleSetDefault(rec.id); }}>
                {rec.is_default
                  ? <StarFilled  style={{ color: '#f59e0b', fontSize: 16 }} />
                  : <StarOutlined style={{ color: '#d1d5db', fontSize: 16 }} />}
              </span>
            </Tooltip>
            <Tooltip title="Edit">
              <Icons.Pencil className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800" onClick={() => openEdit(rec)}/>
            </Tooltip>
            <Popconfirm title="Delete this template?" okText="Yes" cancelText="No" onConfirm={() => handleDelete(rec.id)}>
              <Tooltip title="Delete">
                <Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div
        className="p-2.5 pt-5"
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
      >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: cardBg, minHeight: 'calc(100vh - 120px)' }}
        styles={{ body: { padding: 16 } }}
        title={
          <div className="flex items-center gap-2">
            <Icons.FileText className="w-6 h-6 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0 }}>{t('Report Templates')}</Title>
            <Tag color="blue" style={{ marginLeft: 4, fontSize: 11 }}>{tableData.length} Templates</Tag>
          </div>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!accessData?.create_access}>
            New Template
          </Button>
        }
      >
        <div style={{ paddingRight: 4, paddingBottom: 4 }}>
          <TableContainer
            size="small" bordered loading={loading}
            columns={columns} data={tableData} rowKey="id"
            scrollX={800}
            pagination={{ pageSize: 15 }}
            // Responsive: viewport-anchored so it scales on larger/smaller screens
            height="calc(100vh - 270px)"
            rowClassName={rec => rec.isSystem ? (isDark ? '' : 'bg-teal-50') : rec.isDefault ? (isDark ? '' : 'bg-amber-50') : ''}
          />
        </div>
      </Card>
      </div>

      {/* ══════════════ Modal ══════════════ */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={980}
        centered
        closable={false}
        destroyOnClose
        styles={{
          content: { background: isDark ? defaultColors.palette.gray[900] : '#f0f4f8', padding: 0, borderRadius: 14, overflow: 'hidden' },
          header: { display: 'none' },
          body: { padding: 0 },
        }}
      >
        {/* Header bar */}
        <div style={{ background: '#204448', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={16} color="#ffffff" />
            </div>
            <div>
              <div style={{ color: '#ffffff', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
                {editingId ? 'Edit Template' : 'New Report Template'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Configure header, footer and appearance</div>
            </div>
          </div>
          <button
            onClick={() => setModalOpen(false)}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#ffffff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', maxHeight: '62vh', overflow: 'hidden' }}>

          {/* Left — scrollable form */}
          <div style={{ flex: 1, padding: '14px 16px 0 16px', overflowY: 'auto', minWidth: 0 }}>

            {/* Basic info */}
            <Section accentColor="#3b82f6" icon={Type} title="Basic Information" cardBg={cardBg} borderColor={borderColor} textColor={textColor}>
              <Row gutter={12}>
                <Col span={18}>
                  <FieldLabel required color={inputLabelColor}>Template Name</FieldLabel>
                  <Input
                    placeholder="e.g. Standard BMR Template"
                    value={form.name}
                    onChange={e => setField('name', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <FieldLabel color={inputLabelColor}>Base Font</FieldLabel>
                  <Select value={form.fontSize} onChange={v => setField('fontSize', v)} style={{ width: '100%' }}>
                    {FONT_SIZES.map(s => <Option key={s} value={s}>{s}px</Option>)}
                  </Select>
                </Col>
              </Row>
            </Section>

            {/* Section switcher */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <SectionPill id="header" icon={Layout}      label="Header Settings" activeSection={activeSection} onSelect={setActiveSection} isDark={isDark} subText={subText} />
              <SectionPill id="footer" icon={PanelBottom} label="Footer Settings" activeSection={activeSection} onSelect={setActiveSection} isDark={isDark} subText={subText} />
            </div>

            {/* Header settings */}
            {activeSection === 'header' && (
              <Section accentColor="#204448" icon={Layout} title="Header Settings" cardBg={cardBg} borderColor={borderColor} textColor={textColor}>
                <Row gutter={12}>
                  <Col span={24} style={{ marginBottom: 12 }}>
                    <FieldLabel required color={inputLabelColor}>Header Title</FieldLabel>
                    <Input
                      placeholder="e.g. BATCH MANUFACTURING RECORD"
                      value={form.header.title}
                      onChange={e => setHeaderField('title', e.target.value)}
                    />
                  </Col>
                  <Col span={24} style={{ marginBottom: 14 }}>
                    <FieldLabel color={inputLabelColor}>Logo</FieldLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpg,image/jpeg,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={handleLogoUpload}
                      />
                      <Button
                        icon={<UploadOutlined />}
                        loading={logoUploading}
                        onClick={() => logoInputRef.current?.click()}
                        style={{ borderRadius: 8 }}
                      >
                        {logoUploading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      {form.header.logo ? (
                        <>
                          <CheckCircleFilled style={{ color: '#22c55e', fontSize: 15 }} />
                          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                            {form.header.logo.split('/').pop()}
                          </span>
                          <Button size="small" type="text" danger onClick={() => { setHeaderField('logo', ''); setLogoPreviewUrl(''); }}>Remove</Button>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: subText }}>PNG, JPG, SVG — max 2 MB</span>
                      )}
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ background: sectionBg, borderRadius: 8, padding: '12px 14px', border: `1px solid ${borderColor}` }}>
                      <div style={{ fontSize: 11, color: subText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Appearance</div>
                      <Row gutter={16}>
                        <Col span={8}>
                          <ColorPicker label="Background" value={form.header.bgColor}   onChange={v => setHeaderField('bgColor',   v)} borderColor={borderColor} inputLabelColor={inputLabelColor} />
                        </Col>
                        <Col span={8}>
                          <ColorPicker label="Text Color"  value={form.header.textColor} onChange={v => setHeaderField('textColor', v)} borderColor={borderColor} inputLabelColor={inputLabelColor} />
                        </Col>
                        <Col span={8}>
                          <div style={{ fontSize: 11, color: inputLabelColor, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Font Size</div>
                          <Select value={form.header.fontSize} onChange={v => setHeaderField('fontSize', v)} style={{ width: '100%' }}>
                            {FONT_SIZES.map(s => <Option key={s} value={s}>{s}px</Option>)}
                          </Select>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Section>
            )}

            {/* Footer settings */}
            {activeSection === 'footer' && (
              <Section accentColor="#8b5cf6" icon={PanelBottom} title="Footer Settings" cardBg={cardBg} borderColor={borderColor} textColor={textColor}>
                <Row gutter={12}>
                  <Col span={24} style={{ marginBottom: 14 }}>
                    <FieldLabel color={inputLabelColor}>Footer Text</FieldLabel>
                    <TextArea
                      rows={2}
                      placeholder="e.g. Confidential — For Internal Use Only  |  Page {page} of {total}"
                      value={form.footer.text}
                      onChange={e => setFooterField('text', e.target.value)}
                      style={{ borderRadius: 8 }}
                    />
                    <div style={{ fontSize: 11, color: subText, marginTop: 5 }}>
                      Use{' '}
                      <code style={{ background: isDark ? '#374151' : '#e5e7eb', padding: '1px 5px', borderRadius: 4 }}>{'{page}'}</code>
                      {' '}and{' '}
                      <code style={{ background: isDark ? '#374151' : '#e5e7eb', padding: '1px 5px', borderRadius: 4 }}>{'{total}'}</code>
                      {' '}for automatic page numbers.
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ background: sectionBg, borderRadius: 8, padding: '12px 14px', border: `1px solid ${borderColor}` }}>
                      <div style={{ fontSize: 11, color: subText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Appearance</div>
                      <Row gutter={16}>
                        <Col span={8}>
                          <ColorPicker label="Background" value={form.footer.bgColor}   onChange={v => setFooterField('bgColor',   v)} borderColor={borderColor} inputLabelColor={inputLabelColor} />
                        </Col>
                        <Col span={8}>
                          <ColorPicker label="Text Color"  value={form.footer.textColor} onChange={v => setFooterField('textColor', v)} borderColor={borderColor} inputLabelColor={inputLabelColor} />
                        </Col>
                        <Col span={8}>
                          <div style={{ fontSize: 11, color: inputLabelColor, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Font Size</div>
                          <Select value={form.footer.fontSize} onChange={v => setFooterField('fontSize', v)} style={{ width: '100%' }}>
                            {FONT_SIZES.map(s => <Option key={s} value={s}>{s}px</Option>)}
                          </Select>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Section>
            )}

            <div style={{ height: 16 }} />
          </div>

          {/* Right — preview */}
          <div style={{ width: 320, flexShrink: 0, background: cardBg, borderLeft: `1px solid ${borderColor}`, padding: '14px 14px', overflowY: 'auto' }}>
            <DocPreview form={form} logoPreviewUrl={logoPreviewUrl} sectionBg={sectionBg} borderColor={borderColor} subText={subText} textColor={textColor} />
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ background: cardBg, borderTop: `1px solid ${borderColor}`, padding: '10px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={() => setModalOpen(false)} style={{ borderRadius: 8, minWidth: 90 }}>Cancel</Button>
          <Button
            type="primary" onClick={handleSave} loading={saving} icon={<Save size={14} />}
            style={{ borderRadius: 8, minWidth: 140, background: '#204448', borderColor: '#204448' }}
          >
            {editingId ? 'Update Template' : 'Save Template'}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ReportTemplateConfig;
